import { CampaignState, CampaignType } from 'modules/domains/campaign';
import { MetricIdentifier, SummaryCardWinner } from 'modules/experiments/constants';
import {
  AnalyticsMetric,
  ExperimentHoldoutDefinition,
  ExperimentState,
  ExperimentType,
  MessageMedium,
  MetricDisplayType,
} from 'modules/graphql';
import { DateType } from 'ui/datePicker';
import { BaseOption, SelectValueType } from 'ui/selects';
import { Nullable } from 'utils/tsHelpers';
import {
  BasePageExperimentHoldoutDefinition,
  BasePageExperimentMediumContent,
} from './analyticsBasePage';

export type TabType = {
  id: EXPERIMENT_ANALYTICS_TAB;
  label: string;
};

export enum EXPERIMENT_ANALYTICS_TAB {
  OVERVIEW = 'overview',
  PERFORMANCE = 'performance',
}

export type ExperimentAnalyticsTranslation = {
  badge: Record<string, string>;
  detailsMetadata: Record<string, string>;
  experimentType: Record<string, string>;
  monitor: {
    export: {
      defaultHeaders: Record<string, string>;
    };
  };
  metrics: Record<string, string>;
  notSet: Record<string, string>;
  placeholder: Record<string, string>;
};

export type ExperimentAnalyticsTranslationState = {
  metricTranslations: Record<string, string>;
  translations: ExperimentAnalyticsTranslation;
};

export type ExperimentAnalyticsMetricsState = AnalyticsMetric[];

export type AnalyticsQuery = {
  id: number;
  startTime: DateType;
  endTime: DateType;
};

export type PerformanceTabQuery = {
  id: number;
  startTime: DateType;
  endTime: DateType;
  messageMedium: MessageMedium;
};

export type AnalyticsI18nPayload = {
  translations: ExperimentAnalyticsTranslation;
  metricResourceBundle: Record<string, string>;
};

export type ExperimentMonitorHeaderMetaData = {
  name: string;
  id: number;
  messageMedium: MessageMedium;
  createdAt: string;
  campaignName: string;
  campaignType: CampaignType;
  campaignId: number;
  experimentState: ExperimentState;
  campaignState: CampaignState;
};

export type ExperimentTimeFilterMetaData = {
  variationCount: number;
  experimentStartDate: string;
  experimentEndDate: string;
  experimentState: ExperimentState;
};

export type MetricOption<TValue extends SelectValueType = string> = BaseOption<TValue> & {
  data: {
    identifier: MetricIdentifier;
  };
};

export type ExperimentMetricsOptionsAndDefault = {
  options: MetricOption<string>[];
  defaultValue: string[];
};

export type ExperimentDetailsCardMetadata = {
  experimentType: ExperimentType;
  experimentState: ExperimentState;
  experimentTestPeriod: number;
  experimentStart: Nullable<string>;
  experimentID: number;
  createdBy: string;
  winningCriteria: string;
  holdout: Nullable<ExperimentHoldoutDefinition> | Nullable<BasePageExperimentHoldoutDefinition>;
  experimentEnd: Nullable<string>;
  randomlySplitVariants: boolean;
  initialSendSize: number;
  campaignType: CampaignType;
  testSendsPerVariation: Nullable<number>;
  attributionPeriodHours: Nullable<number>;
};

export interface ExperimentVariantDetails {
  campaignID: number;
  campaignName: string;
  variantTemplateID: number;
  variantName: string;
  testSubject: ExperimentType;
  content: BasePageExperimentMediumContent;
  templateThumbnailUrl: string;
  control: boolean;
  winner: boolean;
  sendTime: Nullable<string>;
  sendTimeUseRecipientTimeZones: boolean;
  messageMedium: MessageMedium;
}

export interface ExperimentVariantSummary extends ExperimentVariantDetails {
  experimentID: number;
  sends: number;
  experimentWinningCriteria: number;
  controlExperimentSends: number;
  controlExperimentWinningCriteria: number;
}

export interface ExperimentHoldout {
  userCount: Nullable<number>;
  winningCriteriaCount: Nullable<number>;
  holdoutGroupPercent: number;
}

export type ExperimentMetricsData = {
  [key in MetricIdentifier]: ExperimentVariantResultRecord;
};

export type ExperimentHoldoutBreakdownDetail = ExperimentHoldout & ExperimentMetricsData;

export type ExperimentSummaryArray =
  | [...ExperimentVariantSummary[], ExperimentHoldout]
  | ExperimentVariantSummary[];

export type ExperimentVariantResult = {
  value: number;
  metricDisplayType: MetricDisplayType;
  histogram: [number, number][];
};

export type ExperimentVariantResultRecord = Record<string, ExperimentVariantResult>;

export type ExperimentVariantBreakdownDetail = ExperimentVariantDetails & ExperimentMetricsData;

export type ExperimentVariantBreakdownArray =
  | [...ExperimentVariantBreakdownDetail[], ExperimentHoldoutBreakdownDetail]
  | ExperimentVariantBreakdownDetail[];

export interface ExperimentBadgeMetadata {
  experimentState: ExperimentState;
  variantWinnerType: SummaryCardWinner;
  randomlySplitVariations: boolean;
  campaignType: CampaignType;
  winnerName: string;
  winnerLift: string;
  experimentTestPeriod: number;
  totalUserSends: number;
  minutesLeftInExperiment: number;
  testSendsPerVariation: Nullable<number>;
}

export enum EXPORT_TABLE_DEFAULT_HEADER {
  CAMPAIGN_ID = 'campaignId',
  CAMPAIGN_NAME = 'campaignName',
  TEMPLATE_ID = 'templateId',
  VARIANT_NAME = 'variantName',
  VARIANT_LINK = 'variantLink',
  VARIANT_STATUS = 'variantStatus',
  TEST_SUBJECT = 'testSubject',
  TEST_SUBJECT_DETAILS = 'testSubjectDetails',
}

export type ExperimentTableDefaultExportHeaders = {
  label: string;
  key: EXPORT_TABLE_DEFAULT_HEADER | ExperimentType;
};

export type ExperimentTableDefaultExportData = {
  [k in EXPORT_TABLE_DEFAULT_HEADER | ExperimentType | string]?: any;
};

export type CampaignConversion = {
  id: number;
  name: string;
};
